import { shapeMessage } from "./messages";
import type { ProofRole } from "./enums";

// vaccination-cards — A DOG'S CARD, ONE RULE FOR BOTH SIDES OF THE GLASS.
//
// The desk's Vaccinations card, the customer's dogs drawer, Home's notices and
// the two upload endpoints all ask one question of a dog's card proofs: where
// does it stand. Answered here once, as `proofs.ts` answers it for the money,
// so the desk can never read *To check* while the phone offers an upload.
//
// EVERY ATTEMPT IS A ROW (0026): a card is never replaced, and its verdict is
// stamped on it. The desk's × removes a card and the attempts behind it
// (Freddi, 2026-10-13); there is no Undo.

/** A card proof as every reader sees it — one row per attempt, newest first where it is listed. */
export interface CardProof {
  id: string;
  dogId: string;
  createdAt: string;
  uploadedBy: string;
  uploadedRole: ProofRole;
  mime: string;
  /** The dates the customer typed with it, in the `"MAR 27"` shape `dogs.vaccines_due` uses. */
  claimedValidUntil: string | null;
  claimedKcUntil: string | null;
  verifiedAt: string | null;
  verifiedBy: string | null;
  rejectedAt: string | null;
  /** The line Patricia was given to send when she said no — the app never sends it (231). */
  rejectedReason: string | null;
  undoneAt: string | null;
}

export type CardVerdict = "to_check" | "accepted" | "not_accepted";

export function cardVerdict(p: Pick<CardProof, "verifiedAt" | "rejectedAt">): CardVerdict {
  if (p.verifiedAt) return "accepted";
  if (p.rejectedAt) return "not_accepted";
  return "to_check";
}

/**
 * Where a dog's card stands. `typed` is a date the desk entered with no card
 * behind it (AC16); `paused` is the third card in a row not accepted (AC9) —
 * uploads stop and the customer is sent to Patricia.
 */
export type DogCardState = "none" | "typed" | "to_check" | "accepted" | "not_accepted" | "paused";

/** Three cards in a row not accepted, and the customer's uploads pause (AC9). */
export const CARD_PAUSE_AFTER = 3;

export interface DogCard {
  state: DogCardState;
  /** The newest card, the one the state is about; null when there is none. */
  proof: CardProof | null;
  /** How many of the newest cards in a row were not accepted. */
  refusedInARow: number;
  /** This dog's cards, newest first. */
  history: CardProof[];
}

// Newest first. A tie on the wire is two cards in one millisecond, and the
// wire's own order breaks it: every reader sends them `ORDER BY created_at DESC,
// id DESC` on Postgres's microseconds, which `toISOString` cannot carry, and a
// stable sort keeps that order. Breaking the tie by id here put the older card
// first whenever its id sorted higher (PR #260 review).
const newestFirst = (a: CardProof, b: CardProof) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0);

export function dogCard(proofs: readonly CardProof[], dog: { id: string; vaccinesDue?: string | null }): DogCard {
  const history = proofs.filter((p) => p.dogId === dog.id).slice().sort(newestFirst);
  const proof = history[0] ?? null;
  if (!proof) return { state: dog.vaccinesDue ? "typed" : "none", proof: null, refusedInARow: 0, history };
  let refusedInARow = 0;
  for (const p of history) {
    if (cardVerdict(p) !== "not_accepted") break;
    refusedInARow++;
  }
  const verdict = cardVerdict(proof);
  const state: DogCardState = verdict === "not_accepted" && refusedInARow >= CARD_PAUSE_AFTER ? "paused" : verdict;
  return { state, proof, refusedInARow, history };
}

/**
 * Can the customer send a card for this dog now? Not while one waits on
 * Patricia (the card's own *checking* state, `c4w`), and not while uploads are
 * paused (AC9). After a no, or over an accepted card that has since run out,
 * yes (AC10, `c4d`).
 */
export function customerMayUpload(card: Pick<DogCard, "state">): boolean {
  return card.state !== "to_check" && card.state !== "paused";
}

// The codes the drawer reads from a refused upload.
export const CARD_PENDING_CODE = "card_pending";
export const CARD_PAUSED_CODE = "card_paused";
/** An accepted card that covers the next stay stands; the customer is not asked for another (PR #260 review). */
export const CARD_CURRENT_CODE = "card_current";
/** A verdict on a card that is no longer the dog's newest (PR #260 review). */
export const CARD_SUPERSEDED_CODE = "card_superseded";

/** Patricia's words after a no, ready to send — the app copies them to her and sends nothing (AC6, 231). */
export function cardNotAcceptedLine(first: string, dog: string): string {
  // messages-one-shape: three paragraphs, never the link line (AC6) — the slot adds it.
  return shapeMessage({ first, fact: `sorry — I couldn’t accept ${dog}’s vaccination card`, ask: "Could you upload the new one in the app?" });
}
